import { Skeleton } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { AppContext } from 'providers/context/App'
import { anyEsth } from 'providers/context/EstheticianList'
import { getCart } from 'services/Cart'
import {
  CUSTOMER_SESSION_TOKEN,
  ENHANCEMENT_ROUTE,
  ESTHETICIAN_LIST_ROUTE,
  LASER_ESTHETICIAN_LIST_ROUTE,
  LOCATIONS_ROUTE,
} from 'utils/constants/Helpers'
import { serviceIDs } from 'utils/constants/services'

import { KLAVIYO_EVENTS, MALIBLE_PAGE } from 'utils/constants/Enums'
import { generateAbandonUrl, getLocalstorageItem } from 'utils/helper-functions'
import { triggerKlaviyoEvent } from 'utils/helper-functions/klaviyoEvents'

import { getPageData } from 'services/MalibleConfig'
import { FAILED_FETCH_SERVICE_PAGE } from 'utils/sentry/sentry-messages'
import { captureError } from 'utils/sentry/sentry-utils'
import { Facial as FacialType, HeaderTheme } from 'utils/types/appTypes'
import { Esthetician } from 'utils/types/estheticianTypes'
import { KlaviyoDataPayload } from 'utils/types/klaviyoPayloadTypes'
import {
  ServiceContent,
  ServicePageResponse,
} from 'utils/types/maliblePageTypes'
import CommonHeader from 'views/components/common-header'
import FacialContinueButton from 'views/components/facial/facialContinueButton'
import FacialList from 'views/components/facial/facialList'
import LocationFacialBar from 'views/components/location-facial-bar'
import { initialServicePageData } from '../locationSelection/__mocks__/mockData'

import './facial.scss'

const DEFAULT_ESTHETICIAN = {
  id: '',
  duration: 0,
  price: 0,
  isAny: false,
  staff: {
    firstName: '',
    lastName: '',
    bio: '',
    id: '',
  },
}

type FacialServiceType = FacialType & ServiceContent

export default function Facial() {
  const { appMasterData, updateData } = useContext(AppContext)
  const navigate = useNavigate()

  const [servicePageData, setServicePageData] = useState<ServicePageResponse>(
    initialServicePageData
  )
  const [loading, setLoading] = useState(false)

  const {
    selectedFacial,
    isLaserFacial,
    availableFacials,
    location: selectedLocation,
    cart,
  } = appMasterData

  const { title, heading, subheading, services } = servicePageData

  const updateFacialState = (
    facial: FacialServiceType,
    facialData: FacialType,
    staffVariants: Esthetician[]
  ) => {
    updateData({
      isLaserFacial: facial.category === 'Laser',
      isMicroNeedling: facial.category === 'Microneedling',
      selectedFacial: facial.name,
      selectedService: facial,
      category: {
        categoryId: facialData.id,
        categoryObj: facialData,
      },
      selectedEnhancements: [],
      filteredEstheticians: [],
      availableEstheticians: staffVariants,
      selectedEsthetician: DEFAULT_ESTHETICIAN,
    })
  }

  const fetchStaffVariants = async (facialId: string) => {
    const cartData = await getCart(cart.cartId, facialId)
    const variants = cartData?.cart?.availableItem?.staffVariants || []

    return [anyEsth, ...variants]
  }

  const onSelectFacial = async (facial: FacialServiceType) => {
    const blvdId =
      process.env.REACT_APP_ENV === 'development'
        ? facial.boulevard_sandbox_id
        : facial.boulevard_id
    const facialData = availableFacials.find((fa) => fa.id === blvdId)
    if (!facialData) return

    const staffVariants = await fetchStaffVariants(facialData.id)

    updateFacialState(facial, facialData, staffVariants)
  }

  const sendKlaviyoEvent = async () => {
    const isUserLogin = Boolean(getLocalstorageItem(CUSTOMER_SESSION_TOKEN))
    if (!isUserLogin) return

    const payload: KlaviyoDataPayload = {
      clientId: appMasterData.userInfo.boulevard_id,
      email: appMasterData.userInfo.email,
      location: appMasterData.location.name,
      serviceId: appMasterData.category.categoryId,
      serviceName: appMasterData.category.categoryObj?.name,
      abandonLink: generateAbandonUrl(
        KLAVIYO_EVENTS.BASE_SERVICE_SELECTED,
        appMasterData
      ),
    }

    await triggerKlaviyoEvent(KLAVIYO_EVENTS.BASE_SERVICE_SELECTED, payload)
  }

  const getNextRoute = () => {
    const categoryId = appMasterData?.category?.categoryId

    if (
      appMasterData.isMicroNeedling ||
      serviceIDs?.[6] === categoryId ||
      appMasterData?.selectedService?.category === 'Hydrafacial'
    ) {
      return ESTHETICIAN_LIST_ROUTE
    }

    if (isLaserFacial) {
      return LASER_ESTHETICIAN_LIST_ROUTE
    }

    return ENHANCEMENT_ROUTE
  }

  const proceedNextScreen = async () => {
    await sendKlaviyoEvent()
    navigate(getNextRoute())
  }

  const getServicePage = async () => {
    try {
      setLoading(true)
      const response = await getPageData(MALIBLE_PAGE.SERVICE)
      if (response) {
        setServicePageData(response.content)
      }
    } catch (error) {
      console.error(error)
      captureError(FAILED_FETCH_SERVICE_PAGE, {
        location: servicePageData,
        error,
        functionName: 'getServicePage',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getServicePage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!selectedLocation.locationId) {
      navigate(LOCATIONS_ROUTE)
    }
  }, [selectedLocation, navigate])

  const filteredFacials = availableFacials
    .reduce<FacialServiceType[]>((acc, facial) => {
      const match = services.find((item) =>
        process.env.REACT_APP_ENV === 'development'
          ? item.boulevard_sandbox_id === facial.id
          : item.boulevard_id === facial.id
      )

      if (match) {
        acc.push({ ...facial, ...match })
      }

      return acc
    }, [])
    .sort((a, b) => a.orderAt - b.orderAt)

  return (
    <section className="facial-section">
      <CommonHeader
        backToFunction={() => navigate(LOCATIONS_ROUTE)}
        theme={HeaderTheme.ORANGE}
        hideFacialName
        title={title.value}
        loading={loading}
      />
      <LocationFacialBar />
      <div className="facial-header">
        <h2>
          {loading ? (
            <Skeleton variant="text" height={50} width={325} />
          ) : (
            heading.value
          )}
        </h2>
        <p>
          {loading ? (
            <Skeleton variant="text" height={50} width={325} />
          ) : (
            subheading.value
          )}
        </p>
      </div>
      <div className="facial-content">
        <FacialList
          facials={filteredFacials}
          selectedFacial={selectedFacial}
          onSelectFacial={onSelectFacial}
        />
      </div>
      <FacialContinueButton
        selectedFacial={selectedFacial}
        proceedNextScreen={proceedNextScreen}
      />
    </section>
  )
}
